import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  listProposals, generateProposal, getProposalPdf, proposalFromNegotiation,
  listContractors, listProducts, listNegotiations,
  generateContract, listContracts, getContractPdf, listContractTemplates
} from '../lib/api';

const t = (lang, pl, en) => lang === 'pl' ? pl : en;

export default function Proposals({ lang, tenantId }) {
  const [tab, setTab] = useState('proposals');
  const [proposals, setProposals] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [products, setProducts] = useState([]);
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showGenDialog, setShowGenDialog] = useState(false);

  // Form state
  const [selContractor, setSelContractor] = useState('');
  const [selProducts, setSelProducts] = useState([]);
  const [selNegotiation, setSelNegotiation] = useState('');

  useEffect(() => { loadData(); }, [tab]);

  async function loadData() {
    setLoading(true);
    try {
      const [ctrs, prods, negs] = await Promise.all([
        listContractors(tenantId),
        listProducts(tenantId),
        listNegotiations(tenantId),
      ]);
      setContractors(ctrs);
      setProducts(prods?.products || prods || []);
      setNegotiations(negs);

      if (tab === 'proposals') {
        setProposals(await listProposals());
      } else {
        setContracts(await listContracts());
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handleGenerateProposal() {
    if (!selContractor) return toast.error(t(lang, 'Wybierz kontrahenta', 'Select contractor'));
    setLoading(true);
    try {
      const result = await generateProposal({
        contractor_id: selContractor,
        product_ids: selProducts,
      });
      toast.success(t(lang, 'Oferta wygenerowana!', 'Proposal generated!'));
      setShowGenDialog(false);
      loadData();
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }

  async function handleFromNegotiation() {
    if (!selNegotiation) return toast.error(t(lang, 'Wybierz negocjację', 'Select negotiation'));
    setLoading(true);
    try {
      await proposalFromNegotiation(selNegotiation);
      toast.success(t(lang, 'Oferta z negocjacji wygenerowana!', 'Proposal from negotiation generated!'));
      loadData();
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }

  async function handleGenerateContract() {
    if (!selContractor) return toast.error(t(lang, 'Wybierz kontrahenta', 'Select contractor'));
    setLoading(true);
    try {
      await generateContract({
        contractor_id: selContractor,
        negotiation_id: selNegotiation || null,
      });
      toast.success(t(lang, 'Umowa wygenerowana!', 'Contract generated!'));
      setTab('contracts');
      loadData();
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }

  async function downloadPdf(id, type) {
    try {
      const blob = type === 'proposal' ? await getProposalPdf(id) : await getContractPdf(id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t(lang, 'Błąd pobierania PDF', 'PDF download error')); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t(lang, 'Oferty i Umowy', 'Proposals & Contracts')}</h1>
          <p className="text-muted-foreground text-sm">
            {t(lang, 'Generuj oferty handlowe i umowy z negocjacji', 'Generate commercial proposals and contracts from negotiations')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant={tab === 'proposals' ? 'default' : 'outline'} onClick={() => setTab('proposals')}>
            {t(lang, 'Oferty', 'Proposals')} ({proposals.length})
          </Button>
          <Button variant={tab === 'contracts' ? 'default' : 'outline'} onClick={() => setTab('contracts')}>
            {t(lang, 'Umowy', 'Contracts')} ({contracts.length})
          </Button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <Dialog open={showGenDialog} onOpenChange={setShowGenDialog}>
          <DialogTrigger asChild>
            <Button>{t(lang, '+ Nowa Oferta', '+ New Proposal')}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t(lang, 'Generuj Ofertę Handlową', 'Generate Commercial Proposal')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t(lang, 'Kontrahent', 'Contractor')}</label>
                <Select value={selContractor} onValueChange={setSelContractor}>
                  <SelectTrigger><SelectValue placeholder={t(lang, 'Wybierz...', 'Select...')} /></SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t(lang, 'Produkty (opcja)', 'Products (optional)')}</label>
                <div className="max-h-40 overflow-y-auto border rounded p-2 space-y-1">
                  {Array.isArray(products) && products.map(p => (
                    <label key={p.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={selProducts.includes(p.id)}
                        onChange={e => {
                          if (e.target.checked) setSelProducts([...selProducts, p.id]);
                          else setSelProducts(selProducts.filter(x => x !== p.id));
                        }} />
                      {p.name} — {p.pricing?.retail_pln?.toLocaleString() || '?'} PLN
                    </label>
                  ))}
                </div>
              </div>
              <Button onClick={handleGenerateProposal} disabled={loading} className="w-full">
                {t(lang, 'Generuj Ofertę', 'Generate Proposal')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <div className="flex gap-2 items-center">
          <Select value={selNegotiation} onValueChange={setSelNegotiation}>
            <SelectTrigger className="w-[250px]"><SelectValue placeholder={t(lang, 'Z negocjacji...', 'From negotiation...')} /></SelectTrigger>
            <SelectContent>
              {negotiations.map(n => (
                <SelectItem key={n.id} value={n.id}>{n.title || n.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleFromNegotiation} disabled={!selNegotiation || loading}>
            {t(lang, 'Oferta z negocjacji', 'Proposal from negotiation')}
          </Button>
        </div>

        <Button variant="outline" onClick={handleGenerateContract} disabled={!selContractor || loading}>
          {t(lang, 'Generuj Umowę', 'Generate Contract')}
        </Button>
      </div>

      {/* List */}
      {tab === 'proposals' ? (
        <div className="space-y-3">
          {proposals.length === 0 && (
            <Card><CardContent className="pt-6 text-center text-muted-foreground">
              {t(lang, 'Brak ofert. Wygeneruj pierwszą ofertę handlową.', 'No proposals yet. Generate your first commercial proposal.')}
            </CardContent></Card>
          )}
          {proposals.map(p => (
            <Card key={p.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{p.title || `Oferta #${p.id?.slice(-6)}`}</CardTitle>
                    <CardDescription>{p.contractor_name} | {new Date(p.created_at).toLocaleDateString()}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge>{p.total_value ? `${p.total_value.toLocaleString()} PLN` : ''}</Badge>
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(p.id, 'proposal')}>PDF</Button>
                  </div>
                </div>
              </CardHeader>
              {p.executive_summary && (
                <CardContent><p className="text-sm text-muted-foreground">{p.executive_summary}</p></CardContent>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {contracts.length === 0 && (
            <Card><CardContent className="pt-6 text-center text-muted-foreground">
              {t(lang, 'Brak umów. Wygeneruj pierwszą umowę z negocjacji.', 'No contracts yet. Generate your first contract from a negotiation.')}
            </CardContent></Card>
          )}
          {contracts.map(c => (
            <Card key={c.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{c.title || `Umowa #${c.id?.slice(-6)}`}</CardTitle>
                    <CardDescription>{c.contractor_name} | {c.contract_type} | {new Date(c.created_at).toLocaleDateString()}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{c.status || 'draft'}</Badge>
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(c.id, 'contract')}>PDF</Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
